import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { 
  ArrowLeft,
  User, 
  Plus, 
  Phone, 
  Calendar, 
  AlertTriangle,
  Clock,
  Heart,
  NotebookPen,
  Activity,
  FileText,
  IndianRupee,
  Users,
  X,
  Edit,
  Save,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";

import { usePatientData } from '@/hooks/usePatientData';
import { usePrescriptionActions } from '@/hooks/usePrescriptionActions';

import PrescriptionForm from '@/components/Prescription/PrescriptionForm';
import ViewPrescriptionModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import EditPrescriptionModal from '@/components/Prescription/EditPrescriptionModal';
import VisitTimeline from '@/components/Patient/VisitTimeline';
import AppointmentsTab from '@/components/Appointment/AppointmentsTab';
import BillingTab from '@/components/Patient/BillingTab';
import PatientFormModal from '@/components/Patient/PatientFormModal';

interface PatientDetailsPageProps {
  onNavigateBack?: () => void;
}



export default function PatientDetailsPage({ onNavigateBack }: PatientDetailsPageProps) {
  const { currentUser } = useAuth();
  const { id: patientId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("prescriptions");
  
  // Patient editing state
  const [showEditModal, setShowEditModal] = useState(false);

  // Guard against invalid patient IDs
  if (!patientId || patientId === 'new' || patientId.length < 5) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">Invalid Patient ID</h2>
            <p className="text-muted-foreground mb-4">The patient ID provided is not valid.</p>
            {onNavigateBack && (
              <Button onClick={onNavigateBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  const { 
    patient, visits, appointments, loading, 
    latestBP, latestBPStatus, followUpStatus, medicalAlerts,
    fetchPatientData, getDaysAgo 
  } = usePatientData(patientId);

  const prescriptionActions = usePrescriptionActions(patientId, fetchPatientData);



  // Auto-open prescription form if openPrescription parameter is present
  useEffect(() => {
    if (searchParams.get('openPrescription') === 'true' && patient && !loading) {
      // Check if there's a repeat prescription ID
      const repeatId = searchParams.get('repeat');
      
      if (repeatId) {
        // Find the prescription to repeat from visits
        const prescriptionToRepeat = visits
          .map(visit => visit.prescription)
          .filter(prescription => prescription && prescription.id === repeatId)
          .find(prescription => prescription);
        
        if (prescriptionToRepeat) {
          // Use the copy functionality to pre-fill the form
          prescriptionActions.handleCopyPrescription(prescriptionToRepeat);
        } else {
          console.log('Prescription to repeat not found:', repeatId);
          // Still open the form, just without pre-filled data
          prescriptionActions.setShowNewPrescription(true);
        }
      } else {
        // Open the prescription form without pre-filled data
      prescriptionActions.setShowNewPrescription(true);
      }
      
      // Remove the parameters from URL to clean it up
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('openPrescription');
        if (repeatId) newParams.delete('repeat');
        return newParams;
      });
    }
  }, [patient, loading, visits, searchParams, setSearchParams, prescriptionActions]);

  // Reset activeTab when entering/exiting prescription creation mode
  useEffect(() => {
    if (prescriptionActions.showNewPrescription) {
      setActiveTab('form'); // Start with form view when creating prescription
    } else {
      setActiveTab('prescriptions'); // Return to prescriptions tab when done
    }
  }, [prescriptionActions.showNewPrescription]);



  // Function to load patient data
  const loadPatientData = () => {
    fetchPatientData();
  };

  // Patient editing functions
  const handleEditPatient = () => {
    setShowEditModal(true);
  };

  const handlePatientUpdated = () => {
    fetchPatientData(); // Refresh patient data
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Patient Not Found</h3>
              <p className="text-muted-foreground mb-6">
                The patient you're looking for doesn't exist or couldn't be loaded.
              </p>
            <Button onClick={onNavigateBack || (() => window.history.back())}>
                <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back to Patients
              </Button>
            </div>
          </CardContent>
        </Card>
    );
  }

  return (
      <div className="prescription-form-container">
        {/* Header - Hidden during prescription creation */}
        {!prescriptionActions.showNewPrescription && (
          <div className="flex items-center justify-between mb-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground">Patient Details</h1>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="font-medium">{patient.name}</span>
                  {patient.mrn && (
                    <>
                      <span>•</span>
                      <span>{patient.mrn}</span>
                    </>
                  )}
                  <span>•</span>
                  <span>{patient.age}y {patient.gender}</span>
                  {patient.blood_group && (
                    <>
                      <span>•</span>
                      <span className="text-destructive font-medium">{patient.blood_group}</span>
                    </>
                  )}
                  {visits.length > 0 && (
                    <>
                      <span>•</span>
                      <span>{visits.length} total visits</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {onNavigateBack && (
              <Button variant="outline" onClick={onNavigateBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
          </div>
        )}

        {/* Medical Alerts - Hidden during prescription creation */}
        {!prescriptionActions.showNewPrescription && (medicalAlerts.length > 0 || patient.current_medications) && (
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="text-destructive w-4 h-4" />
              <span className="font-medium text-destructive text-sm">Medical Alerts</span>
            </div>
            <div className="flex flex-wrap gap-4 text-destructive text-sm">
              {medicalAlerts.map((alert, index) => (
                <span key={index}>
                  <strong>{alert.text}:</strong> {alert.details}
                </span>
              ))}
              {patient.current_medications && (
                <span>
                  <strong>Current Meds:</strong> {patient.current_medications}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Compact Main Content Tabs - Hidden during prescription creation */}
        {!prescriptionActions.showNewPrescription && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 bg-muted p-1">
              <TabsTrigger 
                value="prescriptions" 
                className="data-[state=active]:bg-background"
              >
                <div className="flex items-center gap-2">
                  <NotebookPen className="w-4 h-4" />
                  <span className="hidden sm:inline">Visits</span>
                  <span className="sm:hidden text-xs">Visits</span>
                </div>
              </TabsTrigger>
              
              <TabsTrigger 
                value="appointments" 
                className="data-[state=active]:bg-background"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Appointments</span>
                  <span className="sm:hidden text-xs">Appt</span>
                </div>
              </TabsTrigger>
              
              <TabsTrigger 
                value="profile" 
                className="data-[state=active]:bg-background"
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Patient Profile</span>
                  <span className="sm:hidden text-xs">Profile</span>
                </div>
              </TabsTrigger>
              
              <TabsTrigger 
                value="billing" 
                className="data-[state=active]:bg-background"
              >
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4" />
                  <span className="hidden sm:inline">Billing</span>
                  <span className="sm:hidden text-xs">Bills</span>
                </div>
              </TabsTrigger>
            </TabsList>

            {/* Prescriptions Tab Content */}
            <TabsContent value="prescriptions">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <NotebookPen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">
                      Prescriptions
                    </h2>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <span>{visits.length} visits</span>
                      {visits.length > 0 && (
                        <>
                          <span>•</span>
                          <span>Last visit {getDaysAgo(visits[0].date)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Streamlined Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    onClick={prescriptionActions.handleNewPrescription}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Prescription
                  </Button>
                </div>
              </div>

              {/* Visits Timeline */}
              <VisitTimeline
                visits={visits}
                onEditPrescription={prescriptionActions.handleEditPrescription}
                onViewPrescription={prescriptionActions.handleViewPrescription}
                onCopyPrescription={prescriptionActions.handleCopyPrescription}
                showActions={true}
              />
            </TabsContent>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              <AppointmentsTab 
                appointments={appointments} 
                patientId={patientId}
                patientName={patient.name}
                onRefresh={fetchPatientData}
              />
            </TabsContent>

            {/* Profile Tab - Simplified for Integration */}
            <TabsContent value="profile" className="space-y-4">
              {/* Essential Patient Info */}
              <div className="bg-card border border-border p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-muted border border-border rounded-lg flex items-center justify-center text-lg font-semibold text-foreground">
                      {patient.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">{patient.name}</h2>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{patient.age}y • {patient.gender}</span>
                        {patient.blood_group && <span className="text-destructive font-medium">Blood: {patient.blood_group}</span>}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleEditPatient}
                    className="gap-1"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </Button>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="p-3 border border-border">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">{visits.length}</div>
                    <div className="text-xs text-muted-foreground">Total Visits</div>
                  </div>
                </Card>
                
                <Card className="p-3 border border-border">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">{appointments.length}</div>
                    <div className="text-xs text-muted-foreground">Appointments</div>
                  </div>
                </Card>

                <Card className="p-3 border border-border">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">{medicalAlerts.length}</div>
                    <div className="text-xs text-muted-foreground">Alerts</div>
                  </div>
                </Card>

                <Card className="p-3 border border-border">
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground">
                      {visits.length > 0 ? getDaysAgo(visits[0].date).replace(' ago', '') : 'None'}
                    </div>
                    <div className="text-xs text-muted-foreground">Last Visit</div>
                  </div>
                </Card>
              </div>

              {/* Essential Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Contact & Basic Info */}
                <Card className="p-4 border border-border">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    Patient Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    {patient.phone && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <span className="font-medium">{patient.phone}</span>
                      </div>
                    )}
                    {patient.phone_2 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone 2:</span>
                        <span className="font-medium">{patient.phone_2}</span>
                      </div>
                    )}
                    {patient.email && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email:</span>
                        <span className="font-medium">{patient.email}</span>
                      </div>
                    )}
                    {patient.preferred_language && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Language:</span>
                        <span className="font-medium">{patient.preferred_language}</span>
                      </div>
                    )}
                    {patient.address && (
                      <div>
                        <span className="text-muted-foreground">Address:</span>
                        <div className="text-foreground mt-1 text-xs">{patient.address}</div>
                        {(patient.city || patient.pin_code) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {patient.city && <span>{patient.city}</span>}
                            {patient.city && patient.pin_code && <span> - </span>}
                            {patient.pin_code && <span>{patient.pin_code}</span>}
                          </div>
                        )}
                      </div>
                    )}
                    {patient.aadhar_number && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Aadhar:</span>
                        <span className="font-medium font-mono text-xs">{patient.aadhar_number}</span>
                      </div>
                    )}
                    {patient.emergency_contact_name && (
                      <div className="pt-2 border-t border-border">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Emergency:</span>
                          <span className="font-medium">{patient.emergency_contact_name}</span>
                        </div>
                        {patient.emergency_contact_phone && (
                          <div className="text-xs text-muted-foreground">{patient.emergency_contact_phone}</div>
                        )}
                      </div>
                    )}
                    {patient.mrn && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">MRN:</span>
                        <span className="font-mono font-semibold text-primary">{patient.mrn}</span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Medical Summary */}
                <Card className="p-4 border border-border">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-muted-foreground" />
                    Medical Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    {patient.allergies && (
                      <div>
                        <span className="text-destructive font-medium">Allergies:</span>
                        <div className="text-foreground text-xs mt-1">{patient.allergies}</div>
                      </div>
                    )}
                    {patient.current_medications && (
                      <div>
                        <span className="text-primary font-medium">Current Meds:</span>
                        <div className="text-foreground text-xs mt-1">{patient.current_medications}</div>
                      </div>
                    )}
                    {patient.chronic_conditions && (
                      <div>
                        <span className="text-warning font-medium">Conditions:</span>
                        <div className="text-foreground text-xs mt-1">{patient.chronic_conditions}</div>
                      </div>
                    )}
                    {(!patient.allergies && !patient.current_medications && !patient.chronic_conditions) && (
                      <div className="text-muted-foreground italic">No medical alerts recorded</div>
                    )}
                    {visits.length > 0 && (
                      <div className="pt-2 border-t border-border">
                        <span className="text-muted-foreground">Last Visit:</span>
                        <div className="text-foreground text-xs mt-1">
                          {new Date(visits[0].date).toLocaleDateString()} - {getDaysAgo(visits[0].date)}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing">
              {patientId && (
                <BillingTab 
                  patientId={patientId} 
                  patient={{
                    id: patient.id,
                    name: patient.name,
                    phone: patient.phone,
                    address: patient.address
                  }}
                  doctorId={currentUser?.doctor_id || ""} 
                  clinicId={currentUser?.clinic_id || ""} 
                />
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* Prescription Creation - Clean Professional Layout */}
        {prescriptionActions.showNewPrescription && (
          <div className="space-y-3">
            {/* Professional Header with Essential Info */}
            <div className="flex items-center justify-between p-4 bg-primary/10 border border-primary/20 rounded-md shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-md flex items-center justify-center text-sm font-semibold">
                  {patient.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-foreground">New Prescription</h1>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="font-medium">{patient.name}</span>
                    {patient.mrn && (
                      <>
                        <span>•</span>
                        <span className="font-mono text-primary font-semibold">MRN: {patient.mrn}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{patient.age}y {patient.gender}</span>
                    {patient.blood_group && (
                      <>
                        <span>•</span>
                        <span className="text-destructive font-medium">{patient.blood_group}</span>
                      </>
                    )}
                    {visits.length > 0 && (
                      <>
                        <span>•</span>
                        <span>{visits.length} previous visits</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Critical Medical Alerts */}
                {medicalAlerts.length > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {medicalAlerts.length} Alert{medicalAlerts.length > 1 ? 's' : ''}
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={prescriptionActions.handlePrescriptionCancel}>
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>

            {/* Critical Medical Information */}
            {(patient.current_medications || patient.allergies) && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  <span className="font-medium text-warning text-sm">Important Medical Information</span>
                </div>
                <div className="space-y-1 text-sm">
                  {patient.current_medications && (
                    <div>
                      <span className="font-medium text-warning">Current Medications:</span>
                      <span className="text-warning/80 ml-2">{patient.current_medications}</span>
                    </div>
                  )}
                  {patient.allergies && (
                    <div>
                      <span className="font-medium text-warning">Allergies:</span>
                      <span className="text-warning/80 ml-2">{patient.allergies}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Clinical Prescription Form */}
            <div className="bg-background border border-border/20 rounded-md shadow-sm overflow-hidden">
                {patientId && (
                  <PrescriptionForm
                    mode="create"
                    patientId={patientId}
                    initialData={prescriptionActions.copyData || undefined}
                    onSaved={prescriptionActions.handlePrescriptionSaved}
                    onCancel={prescriptionActions.handlePrescriptionCancel}
                    showStickyBar={true}

                  />
                )}
            </div>

            {/* Visit History Reference */}
            {visits.length > 0 && (
              <div className="bg-background border border-border/20 rounded-md shadow-sm">
                <div className="px-4 py-3 border-b border-border/10 bg-primary/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <h2 className="font-medium text-foreground">Previous Visits</h2>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {visits.length} visits
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Review previous prescriptions and patient history
                  </p>
                </div>
                <div className="p-4">
                  <VisitTimeline
                    visits={visits}
                    onEditPrescription={prescriptionActions.handleEditPrescription}
                    onViewPrescription={prescriptionActions.handleViewPrescription}
                    onCopyPrescription={prescriptionActions.handleCopyPrescription}
                    showActions={true}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* View Prescription Modal */}
        {prescriptionActions.viewingPrescription && (
          <ViewPrescriptionModal
            open={true}
            prescription={prescriptionActions.viewingPrescription}
            onClose={prescriptionActions.handleCloseView}
          />
        )}

        {/* Edit Prescription Modal */}
        {prescriptionActions.editingPrescription && (
          <EditPrescriptionModal
            open={true}
            prescription={prescriptionActions.editingPrescription}
            onClose={prescriptionActions.handleEditCancel}
            onUpdated={prescriptionActions.handleEditSaved}
          />
        )}

        {/* Reusable Patient Edit Modal */}
        {patient && (
          <PatientFormModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSuccess={handlePatientUpdated}
            mode="edit"
            patientId={patientId}
            initialData={{
              name: patient.name || '',
              age: patient.age?.toString() || '',
              date_of_birth: patient.date_of_birth || '',
              gender: patient.gender || 'Male',
              phone: patient.phone || '',
              phone_2: patient.phone_2 || '',
              email: patient.email || '',
              address: patient.address || '',
              city: patient.city || '',
              pin_code: patient.pin_code || '',
              preferred_language: patient.preferred_language || 'English',
              aadhar_number: patient.aadhar_number || '',
              blood_group: patient.blood_group || '',
              allergies: patient.allergies || '',
              emergency_contact_name: patient.emergency_contact_name || '',
              emergency_contact_phone: patient.emergency_contact_phone || '',
              payment_preference: patient.payment_preference || 'Cash'
            }}
            showPrescriptionOption={false}
          />
        )}

    </div>
  );
}
